import { get, set } from "lodash";
import { FC, useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  emptyAdditionValue,
  ErrorDisplayUnsavedItems,
  ErrorsDisplayContainer,
  isAdditionValue,
  MuiAlert,
} from "@ryde/controls";
import {
  resolveShouldUpdateFutureBookingsStations,
  useBookingsNavigation,
  useBulkFutureBookingsConfirm,
  useConfirmDirtyField,
  useCrudEditBulk,
  useDynamicFormsBulkActions,
  useModalConfirm,
  useSchema,
  useSelectedIds,
} from "@ryde/redux";
import {
  bulkSelectedControlsAtomFamily,
  ConfirmResult,
  controlNameMetadataResolve,
  dynamicFormBulkUpdateControlsSF,
  errors as errorsConst,
  FIELD_TYPE_IDS,
  FutureBookingsConfirmOutcome,
  getBulkFormFieldName,
  IApiError,
  ICustomField,
  IExtendableDto,
  IPassengerMetadata,
  Log,
  METADATA_KEY,
  normalizeErrors,
  PLURAL_KEY,
  RydeUiUnit,
  selectedTableRowsBulkUpdateDefaultValues,
  SINGLE_KEY,
  transformApiCrudErrorForToast,
} from "@ryde/shared";

import {
  BaseDrawer,
  DrawerContentStyled,
  DrawerEntityDataLoader,
  DrawerHeader,
  FormFooter,
} from "../drawers";
import { FormControlsBulkWrapper } from "../layout-edit";
import {
  doesControlRequireBulkAction,
  isMultiselectBulkFieldType,
} from "../layout-edit/form.controls.bulk.utils";
import { BulkUpdateNoticesStyled } from "./bulk-update-form.drawer.styled";
import {
  clearErrorsOutsideSelectedFields,
  collectFormErrorPaths,
  getSelectedFieldsToTrigger,
  isBulkActionFieldName,
  pickSelectedBulkFormValues,
  validateSelectedBulkActionFields,
  validateSelectedBulkMultiselectValues,
} from "./bulk-update-form.drawer.utils";

const getDefaultValues = (
  defaultValues: IExtendableDto,
  controls: ICustomField[],
): IExtendableDto => {
  return controls.reduce((acc, control) => {
    const path = controlNameMetadataResolve(control);

    // First-row prefill used to leak into ADD/REMOVE. Chips are filled
    // when the bulk action changes (union for REMOVE, empty for ADD/REPLACE).
    if (isMultiselectBulkFieldType(control.formFieldTypeId)) {
      set(acc, path, []);
      return acc;
    }

    const rawValue = get(defaultValues, path) ?? defaultValues[control.name];
    set(acc, path, rawValue);
    return acc;
  }, {} as IExtendableDto);
};

interface IBulkUpdateFormDrawerContent {
  entity: RydeUiUnit;
}

export const BulkUpdateFormDrawerContent: FC<IBulkUpdateFormDrawerContent> = ({
  entity,
}) => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const { isCalendarBookingView } = useBookingsNavigation();
  const { fetchSchema } = useSchema(entity);
  const drawerContentRef = useRef<HTMLElement>(null);

  const { selectedRowsIds: selectedIds, selectedRowsValues } = useSelectedIds();

  const selectedFields = useRecoilValue(bulkSelectedControlsAtomFamily(entity));
  const filteredControls = useRecoilValue(
    dynamicFormBulkUpdateControlsSF(entity),
  );

  const { handleBulkCrudCancel } = useCrudEditBulk(entity);
  const gridDefaultValues = useRecoilValue(
    selectedTableRowsBulkUpdateDefaultValues(entity),
  );

  const defaultValues = useMemo(() => {
    if (entity === "report") {
      return null;
    }

    return isCalendarBookingView
      ? (selectedRowsValues?.[0] ?? null)
      : gridDefaultValues;
  }, [entity, gridDefaultValues, isCalendarBookingView, selectedRowsValues]);

  const methods = useForm<IExtendableDto>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    ...(defaultValues
      ? {
          defaultValues: getDefaultValues(defaultValues, filteredControls),
        }
      : {}),
  });

  const {
    getValues,
    setError,
    clearErrors,
    watch,
    formState: { isDirty, errors },
    trigger,
  } = methods;
  const { bulkUpdate } = useDynamicFormsBulkActions(entity);
  const { confirmBulkFutureBookingsStations } = useBulkFutureBookingsConfirm();

  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: <ErrorDisplayUnsavedItems />,
  });

  useEffect(() => {
    const subscription = watch((formValues, { name }) => {
      if (!name || !isBulkActionFieldName(name)) {
        return;
      }

      if (get(formValues, name)) {
        clearErrors(name);
      }
    });

    return () => subscription.unsubscribe();
  }, [clearErrors, watch]);

  const selectedCount = selectedIds.length;
  const entitiesKey =
    selectedCount === 1 ? `${entity}.${SINGLE_KEY}` : `${entity}.${PLURAL_KEY}`;
  const saveLabel = t("general.BULK_UPDATE_SAVE", {
    count: selectedCount,
    entities: t(entitiesKey),
  });
  const fieldsRelevantForValidation = useMemo(
    () =>
      getSelectedFieldsToTrigger({
        selectedFields,
        controls: filteredControls,
        entity,
      }),
    [selectedFields, filteredControls, entity],
  );
  const blockingErrorPaths = useMemo(() => {
    const relevantFieldsSet = new Set(fieldsRelevantForValidation);

    return collectFormErrorPaths(errors).filter(path =>
      relevantFieldsSet.has(path),
    );
  }, [errors, fieldsRelevantForValidation]);
  const hasBlockingFormErrors = blockingErrorPaths.length > 0;
  const isSaveDisabled = !selectedFields.length || hasBlockingFormErrors;

  useEffect(() => {
    const relevantFieldsSet = new Set(fieldsRelevantForValidation);

    filteredControls.forEach(control => {
      if (!doesControlRequireBulkAction({ control, entity })) {
        return;
      }

      const bulkFieldName = getBulkFormFieldName(
        controlNameMetadataResolve(control),
      );

      if (!relevantFieldsSet.has(bulkFieldName)) {
        clearErrors(bulkFieldName);
      }
    });
  }, [clearErrors, entity, fieldsRelevantForValidation, filteredControls]);

  const scrollErrorsIntoView = (): void => {
    drawerContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSaveClick = async (): Promise<void> => {
    clearErrorsOutsideSelectedFields({
      formErrors: errors,
      selectedFields: fieldsRelevantForValidation,
      clearErrors,
    });

    const validForm = fieldsRelevantForValidation.length
      ? await trigger(fieldsRelevantForValidation)
      : true;
    const areBulkActionsValid = validateSelectedBulkActionFields({
      selectedFields,
      controls: filteredControls,
      entity,
      getValues,
      setError,
      clearErrors,
      t,
    });
    const areBulkMultiselectValuesValid = validateSelectedBulkMultiselectValues(
      {
        selectedFields,
        controls: filteredControls,
        entity,
        getValues,
        setError,
        clearErrors,
        t,
      },
    );

    if (!validForm || !areBulkActionsValid || !areBulkMultiselectValuesValid) {
      requestAnimationFrame(() => {
        scrollErrorsIntoView();
      });
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.BULK_UPDATE_MESSAGE`, {
        entities: t(entitiesKey),
      }),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const bulkFormData = getValues() as Partial<IExtendableDto>;
    const checkedFormData = pickSelectedBulkFormValues(
      bulkFormData,
      selectedFields,
    );

    const sourceStation3Field = `${METADATA_KEY}.sourceStation3Id`;
    if (selectedFields.includes(sourceStation3Field)) {
      if (!checkedFormData.metadata) {
        checkedFormData.metadata = {};
      }
      const metadata = checkedFormData.metadata as IPassengerMetadata;
      if (metadata.sourceStation3Id === undefined) {
        metadata.sourceStation3Id = null;
      }
    }

    // When addition_value is selected but value is undefined/empty,
    //  set it explicitly
    filteredControls
      .filter(
        (c: ICustomField) =>
          c.formFieldTypeId === FIELD_TYPE_IDS.FORM.addition_value &&
          selectedFields.includes(controlNameMetadataResolve(c)),
      )
      .forEach((c: ICustomField) => {
        const fieldName = controlNameMetadataResolve(c);
        const val = get(checkedFormData, fieldName);
        if (val === undefined || !isAdditionValue(val)) {
          set(checkedFormData, fieldName, emptyAdditionValue);
        }
      });

    // RYDE-7929: a bulk station change can strand future bookings on the old station. Ask before
    // cascading, and only when the server says at least one booking would actually move.
    const futureBookingsOutcome = await confirmBulkFutureBookingsStations({
      entity,
      ids: selectedIds,
      formData: checkedFormData,
    });

    // Never guess when the impact is unknown: station propagation defaults to ON server-side, so
    // proceeding would cascade a mass change nobody approved. Abort and let them retry.
    // The API layer already toasted the real server error, so don't stack a generic one on top.
    if (futureBookingsOutcome === FutureBookingsConfirmOutcome.PreviewFailed) {
      return;
    }

    const shouldUpdateFutureBookingsStations =
      resolveShouldUpdateFutureBookingsStations(futureBookingsOutcome);

    if (shouldUpdateFutureBookingsStations !== undefined) {
      // Stated explicitly even when false — station propagation defaults to ON server-side.
      set(
        checkedFormData,
        "shouldUpdateFutureBookingsStations",
        shouldUpdateFutureBookingsStations,
      );
    }

    try {
      await bulkUpdate(selectedIds, checkedFormData);
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      Log.warnAndToast(
        transformApiCrudErrorForToast({
          errorsNormalized,
          tFunction: t,
          fallbackTranslationKey: errorsConst.common.errorBulkUpdatingData,
        }),
        error,
        checkedFormData,
      );
    }
  };

  const closeSidebar = applyIfConfirmed({ isDirty })((): void => {
    handleBulkCrudCancel();
  });

  useEffect(() => {
    if (!filteredControls?.length) {
      fetchSchema();
    }
  }, [fetchSchema, filteredControls?.length]);

  return (
    <BaseDrawer onCloseDrawer={closeSidebar}>
      <DrawerHeader
        onCloseDrawer={closeSidebar}
        title={t(`general.BULK_UPDATE`)}
      />
      <DrawerContentStyled className="drawer-form" ref={drawerContentRef}>
        <BulkUpdateNoticesStyled>
          {hasBlockingFormErrors && (
            <ErrorsDisplayContainer formErrors={errors} />
          )}
          <MuiAlert severity="info">
            <strong>{t("general.BULK_UPDATE_NOTICE_TITLE")}</strong>
            <ul className="bulk-update-notice-list">
              <li>{t("general.BULK_UPDATE_NOTICE_ONLY_SELECTED_FIELDS")}</li>
              <li>{t("general.BULK_UPDATE_NOTICE_EMPTY_FIELDS_CLEAR")}</li>
            </ul>
          </MuiAlert>
        </BulkUpdateNoticesStyled>
        <FormProvider {...methods}>
          {filteredControls?.length ? (
            <FormControlsBulkWrapper
              controls={filteredControls}
              entity={entity}
            />
          ) : (
            <DrawerEntityDataLoader entityTitle={t(`${entity}.single`)} />
          )}
        </FormProvider>
      </DrawerContentStyled>
      <FormFooter
        onSaveClick={onSaveClick}
        isSaveDisabled={isSaveDisabled}
        onCancelClick={closeSidebar}
        saveLabel={saveLabel}
      />
    </BaseDrawer>
  );
};
